import { LicenseBuy, LicenseDetailsBuy } from '../../Models/License/License';
import { DetailsInvoice, Invoice } from '../../Models/Billing/Billing';

import GlobalFunctionHelper from './GlobalFunctionHelper';

import { PriceSetting, ModulePrice, UserTier, Storage } from '../../Models/Maintenance/PriceSettings/PriceSettings';
import { BuyLicenseField, LicenseDetailsBuyField, PriceSettingsField, InvoiceField, GenerateLicenseField, DetailsInvoiceField, BillingLicenseDetailsField } from '../Enums/ParametersEnum';
import { ValidityTypeEnum } from '../Enums/ValidityTypeEnums';

const ComputationsHelper = {
    //Common Computations
    getSelectedStoragePrice: async function (storageCapacity: number, priceSettings: PriceSetting) {
        return priceSettings?.[PriceSettingsField.STORAGES]?.find((scItem: any) => scItem?.[PriceSettingsField.STORAGE] == storageCapacity)?.[PriceSettingsField.STORAGE_PRICE] ?? 0;
    },
    getUserCapacityItem: async function (userCapacity: number, priceSettings: PriceSetting) {
        return priceSettings?.[PriceSettingsField.USER_TIERS]?.find((ucItem: any) => ucItem?.[PriceSettingsField.MIN_USERS] <= userCapacity && ucItem?.[PriceSettingsField.MAX_USERS] >= userCapacity);
    },
    getLastDigit: function (decimalNum: string) {
        let _lastDigit: number | string = decimalNum[decimalNum.length - 1];

        return _lastDigit;
    },
    getAdjuster: async function (lastDigit: number) {
        let adjuster: number = 0;

        if ((lastDigit <= 2 && lastDigit >= 1) || (lastDigit <= 7 && lastDigit >= 6)) {
            adjuster = (lastDigit == 7 ?
                2 : (lastDigit == 6 ?
                    1 : lastDigit
                )
            );
        }

        if ((lastDigit <= 4 && lastDigit >= 3) || (lastDigit <= 9 && lastDigit >= 8)) {
            adjuster = (lastDigit == 8 || lastDigit == 3 ?
                2 : (lastDigit == 9 || lastDigit == 4 ?
                    1 : 0
                )
            );
        }

        return adjuster;
    },
    computeModPrice: function (modulePrice: number, userCapacity: number, isBillYearly: boolean) {
        return (modulePrice * userCapacity ?? 0) * (isBillYearly ? 12 : 1); //if price settings are monthly basis
        // return (modulePrice * userCapacity ?? 0) / (isBillYearly ? 1 : 12); //if price settings are yearly basis
    },
    computeModulePrice: async function (selectedModules: string[], priceSettings: PriceSetting, userCapacity: number, isBillYearly: boolean) {
        let _modulePrice: any = 0;

        if(selectedModules?.length > 0) {
            selectedModules?.forEach((modPublicid: string | number) => {
                let _selectedModuleBasePrice = priceSettings?.[PriceSettingsField.MODULES]?.find((modItem: any) => modItem?.[PriceSettingsField.MODULE_PUBLIC_ID] == modPublicid)?.[PriceSettingsField.MODULE_BASE_PRICE] ?? 0;

                _modulePrice += _selectedModuleBasePrice;
            });
        }

        _modulePrice = this.computeModPrice(_modulePrice, userCapacity, isBillYearly);

        return _modulePrice;
    },
    computeUserCapacityDiscountAmount: async function (modulePrice: number, userCapacityDiscount: number) {
        return modulePrice * GlobalFunctionHelper.percentToDecimal(userCapacityDiscount);
    },
    computeStorageCapacityPrice: function (storageCapacityPrice: number, isBillYearly: boolean) {
        return storageCapacityPrice * (isBillYearly ? 12 : 1); //if price settings are monthly basis
        // return storageCapacityPrice / (isBillYearly ? 1 : 12); //if price settings are yearly basis
    },
    computeModuleSubTotal: async function (modulePrice: number, userCapacityDiscountAmount: number) {
        return modulePrice - userCapacityDiscountAmount;
    },
    computeAnnualBillingDiscountAmount: async function (isBillYearly: boolean, annualDiscountRate: number, modulesSubTotal: number) {
        return isBillYearly ? (GlobalFunctionHelper.percentToDecimal(annualDiscountRate) * modulesSubTotal) : 0;
    },
    computeLineTotal: async function (storageCapacityPrice: number, modulesSubTotal: number, annualBillingDiscountAmount: number) {
        return storageCapacityPrice + (modulesSubTotal - annualBillingDiscountAmount);
    },
    computeSubTotal: async function (isBillYearly: boolean, lineTotal: number, validity: number) {
        return lineTotal * validity;
    },
    computeTaxAmount: async function (taxRate: number, subTotal: number) {
        return GlobalFunctionHelper.percentToDecimal(taxRate) * subTotal;
    },
    computeTotalBeforeRounding: async function (subTotal: number, taxAmount: number, discountAmount: number = 0) {
        return (subTotal + taxAmount) - discountAmount;
    },
    computeAdjustAmount: async function (lastDigit: number, adjuster: number) {
        let newAdjustAmount = 0;

        if ((lastDigit <= 2 && lastDigit >= 1) || (lastDigit <= 7 && lastDigit >= 6)) {
            newAdjustAmount = (adjuster / 100) * -1;
        }

        if ((lastDigit <= 4 && lastDigit >= 3) || (lastDigit <= 9 && lastDigit >= 8)) {
            newAdjustAmount = (adjuster / 100);
        }

        return parseFloat(newAdjustAmount?.toString());
    },
    computeGrandTotal: async function (totalSales: number, adjustAmount: number) {
        return totalSales + adjustAmount;
    },
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    //LICENSES COMPUTATIONS
    buyLicenseComputeAmounts: async function (_formData: LicenseBuy, priceSettings: PriceSetting) {
        let _storageCapacity = _formData?.[BuyLicenseField.LICENSE_DETAILS]?.[LicenseDetailsBuyField.STORAGE_CAPACITY] ?? 0;
        let _userCapacity = _formData?.[BuyLicenseField.LICENSE_DETAILS]?.[LicenseDetailsBuyField.USER_CAPACITY] ?? 0;
        let selectedUserCapacity = await this.getUserCapacityItem(_userCapacity, priceSettings);
        let selectedModules: string[] = _formData?.[BuyLicenseField.LICENSE_DETAILS]?.[LicenseDetailsBuyField.MODULES] ?? [];
        let _validity = _formData?.[BuyLicenseField.LICENSE_DETAILS]?.[LicenseDetailsBuyField.VALIDITY] ?? 0;
        let _validityType = _formData?.[BuyLicenseField.LICENSE_DETAILS]?.[LicenseDetailsBuyField.VALIDITY_TYPE] ?? ValidityTypeEnum.MONTH;
        let _isBillYearly = _validityType == ValidityTypeEnum.YEAR;

        let _modulePrice = await this.computeModulePrice(selectedModules, priceSettings, _userCapacity, _isBillYearly);
        let _getBaseStorageCapacityPrice = await this.getSelectedStoragePrice(_storageCapacity, priceSettings);
        let _storageCapacityPrice = this.computeStorageCapacityPrice(_getBaseStorageCapacityPrice, _isBillYearly);

        let _userCapacityDiscount = selectedUserCapacity?.[PriceSettingsField.USERDISCOUNT] ?? 0;
        let _userCapacityDiscountAmount = await this.computeUserCapacityDiscountAmount(_modulePrice, _userCapacityDiscount);
        let _modulesSubTotal = await this.computeModuleSubTotal(_modulePrice, _userCapacityDiscountAmount);
        let _annualBillingDiscountAmount = await this.computeAnnualBillingDiscountAmount(_isBillYearly, _formData?.[BuyLicenseField.INVOICE]?.[InvoiceField.ANNUAL_DISCOUNT_RATE] ?? 0, _modulesSubTotal);

        let _discount: number = _formData?.[BuyLicenseField.INVOICE]?.[InvoiceField.DISCOUNT_AMOUNT] ?? 0;
        let _lineTotal = await this.computeLineTotal(_storageCapacityPrice, _modulesSubTotal, _annualBillingDiscountAmount);
        let _subTotal = await this.computeSubTotal(_isBillYearly, _lineTotal, _validity);
        let _taxRate = _formData?.[BuyLicenseField.INVOICE]?.[InvoiceField.TAX_RATE] ?? 0;
        let _taxAmount = await this.computeTaxAmount(_taxRate, _subTotal);
        let _totalBeforeRounding = await this.computeTotalBeforeRounding(_subTotal, _taxAmount, _discount);
        let lastDigit: any = this.getLastDigit(_totalBeforeRounding?.toFixed(2));
        let adjuster: number = await this.getAdjuster(isNaN(parseFloat(lastDigit)) ? 0 : parseFloat(lastDigit));
        let newAdjustAmount: number = await this.computeAdjustAmount(isNaN(parseFloat(lastDigit)) ? 0 : parseFloat(lastDigit), adjuster);
        let _totalAfterRounding = await this.computeGrandTotal(_totalBeforeRounding, newAdjustAmount);

        let _invoiceData: Invoice = _formData?.[BuyLicenseField.INVOICE] ?? new Invoice();
        let _licenseDetails: LicenseDetailsBuy = _formData?.[BuyLicenseField.LICENSE_DETAILS] ?? new LicenseDetailsBuy();

        return {
            ..._formData,
            [BuyLicenseField.LICENSE_DETAILS]: {
                ..._licenseDetails,
                [LicenseDetailsBuyField.BILLING_DETAILS]: {
                    ..._licenseDetails?.[LicenseDetailsBuyField.BILLING_DETAILS],
                    [GenerateLicenseField.SUB_TOTAL]: _subTotal,
                    [GenerateLicenseField.TAX_RATE]: _taxRate,
                    [GenerateLicenseField.TAX_AMOUNT]: _taxAmount,
                    [GenerateLicenseField.TOTAL_AMOUNT]: _totalAfterRounding,
                    [GenerateLicenseField.IS_WITH_ANNUAL_DISCOUNT]: _isBillYearly,
                    [GenerateLicenseField.DISCOUNT_APPLIED]: _isBillYearly,
                },
            },
            [BuyLicenseField.INVOICE]: {
                ..._invoiceData,
                [InvoiceField.LINE_TOTAL]: _lineTotal,
                [InvoiceField.SUB_TOTAL]: _subTotal,
                [InvoiceField.TAX_RATE]: _taxRate,
                [InvoiceField.TAX_AMOUNT]: _taxAmount,
                [InvoiceField.TOTAL_BEFORE_ADJUSTMENT]: _totalBeforeRounding,
                [InvoiceField.ROUND_ADJUSTMENT]: newAdjustAmount,
                [InvoiceField.TOTAL_AFTER_ADJUSTMENT]: _totalAfterRounding,
                
                [InvoiceField.USER_CAPACITY_DISCOUNT]: _userCapacityDiscount,
                [InvoiceField.USER_CAPACITY_DISCOUNT_AMOUNT]: _userCapacityDiscountAmount,
                [InvoiceField.ANNUAL_DISCOUNT_AMOUNT]: _annualBillingDiscountAmount,
            },
        };
    },
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    //BILLING COMPUTATIONS
    billingComputeAmounts: async function (_formData: DetailsInvoice, priceSettings: PriceSetting) {
        let _storageCapacity = _formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.STORAGE_CAPACITY] ?? 0;
        let _userCapacity = _formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.USER_CAPACITY] ?? 0;
        let selectedUserCapacity = await this.getUserCapacityItem(_userCapacity, priceSettings);
        let selectedModules: string[] = _formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.MODULES] ?? [];
        let _validity = _formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY] ?? 0;
        let _validityType = _formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY_TYPE] ?? ValidityTypeEnum.MONTH;
        let _isBillYearly = _validityType == ValidityTypeEnum.YEAR;

        let _modulePrice = await this.computeModulePrice(selectedModules, priceSettings, _userCapacity, _isBillYearly);
        let _getBaseStorageCapacityPrice = await this.getSelectedStoragePrice(_storageCapacity, priceSettings);
        let _storageCapacityPrice = this.computeStorageCapacityPrice(_getBaseStorageCapacityPrice, _isBillYearly);

        let _userCapacityDiscount = selectedUserCapacity?.[PriceSettingsField.USERDISCOUNT] ?? 0;
        let _userCapacityDiscountAmount = await this.computeUserCapacityDiscountAmount(_modulePrice, _userCapacityDiscount);
        let _modulesSubTotal = await this.computeModuleSubTotal(_modulePrice, _userCapacityDiscountAmount);
        let _annualBillingDiscountAmount = await this.computeAnnualBillingDiscountAmount(_isBillYearly, _formData?.[DetailsInvoiceField.ANNUAL_DISCOUNT_RATE] ?? 0, _modulesSubTotal);

        let _discount: number = _formData?.[DetailsInvoiceField.DISCOUNT_AMOUNT] ?? 0;
        let _lineTotal = await this.computeLineTotal(_storageCapacityPrice, _modulesSubTotal, _annualBillingDiscountAmount);
        let _subTotal = await this.computeSubTotal(_isBillYearly, _lineTotal, _validity);
        let _taxRate = _formData?.[DetailsInvoiceField.TAX_RATE] ?? 0;
        let _taxAmount = await this.computeTaxAmount(_taxRate, _subTotal);
        let _totalBeforeRounding = await this.computeTotalBeforeRounding(_subTotal, _taxAmount, _discount);
        let lastDigit: any = this.getLastDigit(_totalBeforeRounding?.toFixed(2));
        let adjuster: number = await this.getAdjuster(isNaN(parseFloat(lastDigit)) ? 0 : parseFloat(lastDigit));
        let newAdjustAmount: number = await this.computeAdjustAmount(isNaN(parseFloat(lastDigit)) ? 0 : parseFloat(lastDigit), adjuster);
        let _totalAfterRounding = await this.computeGrandTotal(_totalBeforeRounding, newAdjustAmount);

        return {
            ..._formData,
            [DetailsInvoiceField.IS_WITH_ANNUAL_DISCOUNT]: _isBillYearly,
            [DetailsInvoiceField.USER_CAPACITY_DISCOUNT_AMOUNT]: _userCapacityDiscountAmount,
            [DetailsInvoiceField.USER_CAPACITY_DISCOUNT]: _userCapacityDiscount,
            [DetailsInvoiceField.ANNUAL_DISCOUNT_AMOUNT]: _annualBillingDiscountAmount,

            [DetailsInvoiceField.LINE_TOTAL]: _lineTotal,
            [DetailsInvoiceField.SUB_TOTAL]: _subTotal,
            [DetailsInvoiceField.TAX_RATE]: _taxRate,
            [DetailsInvoiceField.TAX_AMOUNT]: _taxAmount,
            [DetailsInvoiceField.TOTAL_BEFORE_ADJUSTMENT]: _totalBeforeRounding,
            [DetailsInvoiceField.ROUND_ADJUSTMENT]: newAdjustAmount,
            [DetailsInvoiceField.TOTAL_AFTER_ADJUSTMENT]: _totalAfterRounding,
        };
    },
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
}

export function isReactElement(arg: any): arg is React.ReactElement {
    return arg.hasOwnProperty("type");
}

export default ComputationsHelper;